/**
 * Critical Thinking Test (CTT) — simplified (10 items).
 * Mixed response scales: 5-way inference, 2-way assumptions/deductions/arguments/interpretation.
 */

const L = (en, tc, sc) => ({
  en,
  tc: tc != null ? tc : en,
  sc: sc != null ? sc : en
});

const OPT_INFERENCE_5 = [
  { key: 'A', label: L('TRUE', '真', '真') },
  { key: 'B', label: L('PROBABLY TRUE', '可能為真', '可能为真') },
  { key: 'C', label: L('MORE INFORMATION REQUIRED', '需要更多資料', '需要更多资料') },
  { key: 'D', label: L('PROBABLY FALSE', '可能為假', '可能为假') },
  { key: 'E', label: L('FALSE', '假', '假') }
];

const OPT_ASSUMPTION_2 = [
  { key: 'A', label: L('ASSUMPTION MADE', '已作出假定', '已作出假定') },
  { key: 'B', label: L('ASSUMPTION NOT MADE', '未作出假定', '未作出假定') }
];

const OPT_DEDUCTION_2 = [
  { key: 'A', label: L('CONCLUSION FOLLOWS', '結論成立', '结论成立') },
  { key: 'B', label: L('CONCLUSION DOES NOT FOLLOW', '結論不成立', '结论不成立') }
];

const OPT_ARGUMENT_2 = [
  { key: 'A', label: L('STRONG ARGUMENT', '論據有力', '论据有力') },
  { key: 'B', label: L('WEAK ARGUMENT', '論據薄弱', '论据薄弱') }
];

function st(keysToScore) {
  return { ...keysToScore };
}

/**
 * @param {string} id
 * @param {{en:string,tc:string,sc:string}} body
 * @param {number} n passage number (for title)
 */
function passage(id, n, body) {
  return {
    id,
    title: L(`Passage ${n}`, `文章 ${n}`, `文章 ${n}`),
    body
  };
}

/**
 * @param {string} id
 * @param {number} displayNumber
 * @param {string} passageId
 * @param {{en:string,tc:string,sc:string}} prompt
 * @param {{key:string,label:{en:string,tc:string,sc:string}}[]} options
 * @param {Record<string, number>} scoreTable
 */
function q(id, displayNumber, passageId, prompt, options, scoreTable) {
  return {
    id,
    displayNumber,
    passageId,
    prompt,
    options,
    scoreTable
  };
}

const P1 = passage(
  'ctt-p1',
  1,
  L(
    'In science class, students learn that quantum mechanics explains how tiny particles behave. Max Planck started this field in 1900 by showing that energy moves in small, specific packets instead of a continuous stream. Later, Einstein confirmed this by proving light is also made of energy packets called photons.',
    '在科學課上，學生學到量子力學用來解釋微小粒子的行為。普朗克在 1900 年提出能量以離散小包（量子）形式傳遞，而非連續流動。其後，愛因斯坦證明光也由稱為光子的能量小包組成。',
    '在科学课上，学生学到量子力学用来解释微小粒子的行为。普朗克在 1900 年提出能量以离散小包（量子）形式传递，而非连续流动。其后，爱因斯坦证明光也由称为光子的能量小包组成。'
  )
);

const P2 = passage(
  'ctt-p2',
  2,
  L(
    "When saving for the senior trip, it is risky to put all the class money into a single fundraiser, like just a bake sale. If the bake sale fails, all money is lost. It is safer to spread the money across different fundraisers, like car washes and ticket sales. This is called diversifying. The best way to manage this is through the Student Council, which charges low fees, rather than the School Bank, which charges high fees.",
    '為了畢業旅行籌款，把全班資金押在單一活動（例如只做一次義賣）是有風險的：若義賣失敗，資金可能全數損失。較安全的方法是把資金分散到不同的籌款活動（如洗車、售票），這稱為分散風險。文中亦指出，透過收費較低的學生會管理，比透過收費較高的校內銀行更理想。',
    '为了毕业旅行筹款，把全班资金押在单一活动（例如只做一次义卖）是有风险的：若义卖失败，资金可能全数损失。较安全的方法是把资金分散到不同的筹款活动（如洗车、售票），这称为分散风险。文中亦指出，通过收费较低的学生会管理，比通过收费较高的校内银行更理想。'
  )
);

const P3 = passage(
  'ctt-p3',
  3,
  L(
    'Students often drink chamomile tea from the cafeteria to help them relax before big exams. The tea contains a natural plant product called apigenin, which creates a calming effect. Because it has no caffeine and only two calories, it is a popular choice for students trying to reduce pre-test anxiety.',
    '學生在大型考試前常喝食堂的洋甘菊茶來放鬆。茶中含有名為芹菜素（apigenin）的天然植物成分，可帶來鎮靜效果。由於不含咖啡因且熱量很低，對想減輕考前焦慮的學生很受歡迎。',
    '学生在大型考试前常喝食堂的洋甘菊茶来放松。茶中含有名为芹菜素（apigenin）的天然植物成分，可带来镇静效果。由于不含咖啡因且热量很低，对想减轻考前焦虑的学生很受欢迎。'
  )
);

const P4 = passage(
  'ctt-p4',
  4,
  L(
    "In biology, students learn that DNA is the genetic code we inherit from our parents. DNA is built from long chains of four chemicals: A, C, G, and T. While a full DNA chain is three billion chemicals long, a 'gene' is just a small fragment of this chain, usually about a thousand chemicals long. Each gene gives an instruction for how the body is built.",
    '在生物課上，學生學到 DNA 是從父母遺傳而來的基因密碼。DNA 由四種化學物質 A、C、G、T 組成的長鏈構成。完整 DNA 鏈約有三十億個化學單位；而「基因」只是其中一小段，通常約一千個單位長。每個基因提供一項建構身體的指令。',
    '在生物课上，学生学到 DNA 是从父母遗传而来的基因密码。DNA 由四种化学物质 A、C、G、T 组成的长链构成。完整 DNA 链约有三十亿个化学单位；而“基因”只是其中一小段，通常约一千个单位长。每个基因提供一项建构身体的指令。'
  )
);

const P5 = passage(
  'ctt-p5',
  5,
  L(
    "In coding club, students use 'algorithms'—a step-by-step set of instructions to solve a problem. But we use algorithms in real life, too. Following a recipe to bake a cake is an algorithm. When you make a pros-and-cons list to decide which school club to join, you are using an 'intuitive algorithm'. Unlike computer code, human intuitive algorithms are not perfectly precise, but they help us make choices when we are uncertain.",
    '在編程社，學生使用「演算法」——用一步步指令解決問題。但我們在日常生活也會用到演算法：跟食譜烘焙蛋糕是演算法；列出加入哪個社團的利弊清單，是一種「直覺演算法」。與電腦程式不同，人類的直覺演算法不會完全精確，但能在不確定時幫助我們做選擇。',
    '在编程社，学生使用“算法”——用一步步指令解决问题。但我们在日常生活也会用到算法：跟食谱烘焙蛋糕是算法；列出加入哪个社团的利弊清单，是一种“直觉算法”。与电脑程序不同，人类的直觉算法不会完全精确，但能在不确定时帮助我们做选择。'
  )
);

const P6 = passage(
  'ctt-p6',
  6,
  L(
    "There is a theory about the school's annual 'Guess the Jellybeans' contest. The theory says that if you average out everyone's guesses, the collective group guess is always perfectly accurate. Because the crowd is so accurate, it is almost impossible for one single student to 'beat the crowd' and guess much better than the average. Therefore, the best strategy is to just submit the average group guess.",
    '學校一年一度的「猜糖果數量」比賽有一個理論：把所有人的猜測取平均，群體的平均猜測總是非常準確。由於群體如此準確，單一學生幾乎不可能大幅優於平均值。因此，最佳策略就是提交群體平均猜測。',
    '学校一年一度的“猜糖果数量”比赛有一个理论：把所有人的猜测取平均，群体的平均猜测总是非常准确。由于群体如此准确，单一学生几乎不可能大幅优于平均值。因此，最佳策略就是提交群体平均猜测。'
  )
);

const P7 = passage(
  'ctt-p7',
  7,
  L(
    'To win a Student Council election, candidates need a campaign message that students actually want to share. A boring poster won’t go viral. Messages that are funny, inspiring, or thought-provoking are the ones that spread across the school. Candidates must think creatively and ask themselves: What motivates my classmates? Sometimes, the best campaign strategy is to just support a popular trend that students are already doing.',
    '要在學生會選舉中勝出，候選人需要同學願意分享的競選訊息。沉悶的海報不會「爆紅」。有趣、鼓舞或發人深省的訊息才會在校園傳播。候選人必須創意思考：甚麼能驅動同學？有時最佳策略是順勢支持同學已在做的熱門潮流。',
    '要在学生会选举中胜出，候选人需要同学愿意分享的竞选讯息。沉闷的海报不会“爆红”。有趣、鼓舞或发人深省的讯息才会在校园传播。候选人必须创意思考：什么能驱动同学？有时最佳策略是顺势支持同学已在做的热门潮流。'
  )
);

const P8 = passage(
  'ctt-p8',
  8,
  L(
    "For years, the school treasurer kept a private notebook of all club funds. Because it was private, we had to trust the treasurer to be honest. However, some past treasurers secretly changed the numbers to hide missing money. To fix this, the school moved to a 'Blockchain Board'. Now, every single club expense is posted publicly on a digital board that everyone can see. Because it is public and unchangeable, no one can secretly alter the numbers, removing the need for blind trust.",
    '多年來，學校司庫把社團資金記在私人筆記本內，因為不公開，大家只能相信司庫誠實。但過去有司庫暗中改數字以掩蓋款項短缺。為解決問題，學校改用「區塊鏈公告板」：每筆支出都公開張貼在人人可見的數碼板上，且不可更改，因此無人能暗中篡改數字，亦不必再盲目信任。',
    '多年来，学校司库把社团资金记在私人笔记本内，因为不公开，大家只能相信司库诚实。但过去有司库暗中改数字以掩盖款项短缺。为解决问题，学校改用“区块链公告板”：每笔支出都公开张贴在人人可见的数字板上，且不可更改，因此无人能暗中篡改数字，也不必再盲目信任。'
  )
);

const P9 = passage(
  'ctt-p9',
  9,
  L(
    "When facing a setback, positive thinkers cope better than negative thinkers. Researchers found that when optimistic students fail to make the varsity sports team, they focus on how to improve their skills and plan to try out again next year. Conversely, pessimistic students assume the coach simply doesn't like them, feel the situation is out of their control, and give up on playing the sport entirely.",
    '面對挫折時，正向思考者比負向思考者更能應對。研究發現：樂觀的學生若未能入選校隊，會專注提升技能並計劃來年再試；相反，悲觀的學生會認為教練不喜歡自己、覺得情況無法控制，甚至完全放棄該運動。',
    '面对挫折时，正向思考者比负向思考者更能应对。研究发现：乐观的学生若未能入选校队，会专注提升技能并计划来年再试；相反，悲观的学生会认为教练不喜欢自己、觉得情况无法控制，甚至完全放弃该运动。'
  )
);

const P10 = passage(
  'ctt-p10',
  10,
  L(
    "The school cafeteria payment system has evolved. Years ago, students paid with physical cash and coins at the counter. As the school grew larger, cash lines became too slow. The school replaced cash with digital 'lunch cards' based on a credit system. Students scan the card, and their parents pay the bill at the end of the month. However, this credit system only works because the school trusts that the parents will eventually pay.",
    '學校食堂付款系統不斷演變。過去學生在櫃檯用現金與硬幣付款；學校變大後，排隊太慢。學校改用以信用為基礎的電子「午餐卡」：學生掃卡，家長月尾結算。然而，這套信用制度能運作，是因為學校信任家長最終會付款。',
    '学校食堂付款系统不断演变。过去学生在柜台用现金与硬币付款；学校变大后，排队太慢。学校改用以信用为基础的电子“午餐卡”：学生扫卡，家长月尾结算。然而，这套信用制度能运作，是因为学校信任家长最终会付款。'
  )
);

const QUESTIONS = [
  q(
    'ctt-q1',
    1,
    'ctt-p1',
    L(
      'Quantum mechanics explains the entire structure of the massive universe.',
      '量子力學能解釋整個龐大宇宙的全部結構。',
      '量子力学能解释整个庞大宇宙的全部结构。'
    ),
    OPT_INFERENCE_5,
    st({ A: 0, B: 0, C: 0, D: 0, E: 1 })
  ),
  q(
    'ctt-q2',
    2,
    'ctt-p2',
    L(
      'Spreading money across multiple fundraisers guarantees you will make a huge profit.',
      '把資金分散到多個籌款活動能保證你一定賺到巨額利潤。',
      '把资金分散到多个筹款活动能保证你一定赚到巨额利润。'
    ),
    OPT_INFERENCE_5,
    st({ A: 0, B: 0, C: 0, D: 0, E: 1 })
  ),
  q(
    'ctt-q3',
    3,
    'ctt-p3',
    L(
      'Drinking chamomile tea causes you to fall asleep.',
      '飲用洋甘菊茶會令你入睡。',
      '饮用洋甘菊茶会令你入睡。'
    ),
    OPT_ASSUMPTION_2,
    st({ A: 1, B: 0 })
  ),
  q(
    'ctt-q4',
    4,
    'ctt-p4',
    L(
      'People with identical DNA always have the exact same personality.',
      '擁有完全相同 DNA 的人必定擁有一模一樣的性格。',
      '拥有完全相同 DNA 的人必定拥有一模一样的性格。'
    ),
    OPT_ASSUMPTION_2,
    st({ A: 0, B: 1 })
  ),
  q(
    'ctt-q5',
    5,
    'ctt-p5',
    L(
      'Computer algorithms always make better life choices than human intuitive algorithms.',
      '電腦演算法在生活選擇上總是比人類的直覺演算法更好。',
      '电脑算法在生活选择上总是比人类的直觉算法更好。'
    ),
    OPT_DEDUCTION_2,
    st({ A: 0, B: 1 })
  ),
  q(
    'ctt-q6',
    6,
    'ctt-p6',
    L(
      "The average of all students' guesses provides a highly accurate estimate of the jellybeans.",
      '所有學生猜測的平均值能提供對糖果數量的高度準確估計。',
      '所有学生猜测的平均值能提供对糖果数量的高度准确估计。'
    ),
    OPT_DEDUCTION_2,
    st({ A: 1, B: 0 })
  ),
  q(
    'ctt-q7',
    7,
    'ctt-p7',
    L(
      'Having the most colorful posters guarantees a candidate will win the election.',
      '擁有最繽紛的海報能保證候選人勝出選舉。',
      '拥有最缤纷的海报能保证候选人胜出选举。'
    ),
    OPT_ARGUMENT_2,
    st({ A: 0, B: 1 })
  ),
  q(
    'ctt-q8',
    8,
    'ctt-p8',
    L(
      'A public digital board is an effective way to prevent treasurers from secretly hiding missing money.',
      '公開的數碼公告板能有效防止司庫暗中隱瞞短缺款項。',
      '公开的数字公告板能有效防止司库暗中隐瞒短缺款项。'
    ),
    OPT_ARGUMENT_2,
    st({ A: 1, B: 0 })
  ),
  q(
    'ctt-q9',
    9,
    'ctt-p9',
    L(
      'Optimistic students will automatically become professional athletes when they grow up.',
      '樂觀的學生長大後會自動成為職業運動員。',
      '乐观的学生长大后会自动成为职业运动员。'
    ),
    OPT_DEDUCTION_2,
    st({ A: 0, B: 1 })
  ),
  q(
    'ctt-q10',
    10,
    'ctt-p10',
    L(
      'The school switched to digital cards because physical cash is completely obsolete everywhere.',
      '學校改用電子卡是因為現金在任何地方都已完全過時。',
      '学校改用电子卡是因为现金在任何地方都已完全过时。'
    ),
    OPT_DEDUCTION_2,
    st({ A: 0, B: 1 })
  )
];

export const criticalThinkingTestPaper = {
  slug: 'critical-thinking',
  title: L('Critical Thinking', '批判性思維', '批判性思维'),
  paperInstruction: L(
    'Read each passage and answer the question that follows. Choose the single best option based only on the information provided.',
    '閱讀每段文章並回答其後問題。請僅根據文中資訊選出唯一最佳答案。',
    '阅读每段文章并回答其后问题。请仅根据文中信息选出唯一最佳答案。'
  ),
  timeLimitSeconds: 3600,
  order: 1,
  isPublished: true,
  sections: [
    {
      id: 'ctt-simplified',
      title: L(
        'Evaluating arguments',
        '案例：論證評估',
        '案例：论证评估'
      ),
      passages: [P1, P2, P3, P4, P5, P6, P7, P8, P9, P10],
      questions: QUESTIONS
    }
  ]
};

